if (!customElements.get('media-gallery')) {
  customElements.define(
    'media-gallery',
    class MediaGallery extends HTMLElement {
      constructor() {
        super();
        this.elements = {
          liveRegion: this.querySelector('[id^="GalleryStatus"]'),
          viewer: this.querySelector('[id^="GalleryViewer"]'),
          thumbnails: this.querySelector('[id^="GalleryThumbnails"]'),
        };
        this.mql = window.matchMedia('(min-width: 750px)');
        if (this.elements.thumbnails) {
          this.elements.viewer.addEventListener('slideChanged', debounce(this.onSlideChanged.bind(this), 500));
          this.elements.thumbnails.querySelectorAll('[data-target]').forEach((mediaToSwitch) => {
            mediaToSwitch
              .querySelector('button')
              .addEventListener('click', this.setActiveMedia.bind(this, mediaToSwitch.dataset.target, false));
          });
          if (this.dataset.desktopLayout.includes('thumbnail') && this.mql.matches) this.removeListSemantic();
        }

        if (this.dataset.mediaBlock === 'true') {
          this.elements.viewer?.querySelectorAll('.slider-button').forEach((button) => {
            button.addEventListener('click', (event) => {
              if (!this.mql.matches) return;
              event.preventDefault();
              event.stopImmediatePropagation();
              const slides = [...this.elements.viewer.querySelectorAll('.product__media-item[data-media-id]')];
              const index = slides.findIndex((slide) => slide.classList.contains('is-active'));
              const next = slides[index + (button.name === 'next' ? 1 : -1)];
              if (next) this.setActiveMedia(next.dataset.mediaId, false);
            }, true);
          });
          this.onViewportChange = () => {
            cancelAnimationFrame(this.navigationFrame);
            this.navigationFrame = requestAnimationFrame(() => this.updateConfiguredNavigation());
          };
          this.mql.addEventListener('change', this.onViewportChange);
          this.navigationResizeObserver = new ResizeObserver(this.onViewportChange);
          if (this.elements.viewer) this.navigationResizeObserver.observe(this.elements.viewer);
          this.updateConfiguredNavigation();
        }
      }

      static get observedAttributes() {
        return ['data-gallery-autoplay', 'data-gallery-autoplay-interval'];
      }

      connectedCallback() {
        this.setupAutoplay();
      }

      attributeChangedCallback() {
        if (this.isConnected) this.setupAutoplay();
      }

      setupAutoplay() {
        window.clearInterval(this.autoplayTimer);
        window.clearTimeout(this.autoplayResumeTimer);
        this.autoplayController?.abort();
        const viewer = this.elements.viewer;
        if (!viewer || this.dataset.galleryAutoplay === 'false') return;
        const interval = Math.min(15, Math.max(2, Number(this.dataset.galleryAutoplayInterval) || 5));
        this.autoplayController = new AbortController();
        const signal = this.autoplayController.signal;

        const pause = () => {
          this.autoplayPaused = true;
        };
        const resume = () => {
          this.autoplayPaused = false;
        };

        this.autoplayPaused = false;
        this.autoplayResumeTimer = null;
        this.addEventListener('mouseenter', pause, { signal });
        this.addEventListener('mouseleave', resume, { signal });
        this.addEventListener('touchstart', pause, { passive: true, signal });
        this.addEventListener(
          'touchend',
          () => {
            window.clearTimeout(this.autoplayResumeTimer);
            this.autoplayResumeTimer = window.setTimeout(resume, 15000);
          },
          { passive: true, signal }
        );

        // Keep manual navigation and interaction pauses independent of autoplay.
        this.autoplayTimer = window.setInterval(() => {
          if (this.autoplayPaused) return;

          const nextButton = viewer.querySelector('button[name="next"]');
          const slides = viewer.querySelectorAll('[id^="Slide-"]');
          if (!nextButton || slides.length < 2) return;

          if (!nextButton.disabled && nextButton.getAttribute('aria-disabled') !== 'true') {
            nextButton.click();
            return;
          }

          const previousButton = viewer.querySelector('button[name="previous"]');
          if (!previousButton) return;
          for (let index = 0; index < slides.length; index += 1) previousButton.click();
        }, interval * 1000);
      }

      updateConfiguredNavigation() {
        if (this.dataset.mediaBlock !== 'true' || !this.mql.matches || !this.elements.viewer) return;
        const slides = [...this.elements.viewer.querySelectorAll('.product__media-item[data-media-id]')];
        const index = Math.max(0, slides.findIndex((slide) => slide.classList.contains('is-active')));
        const current = this.elements.viewer.querySelector('.slider-counter--current');
        const total = this.elements.viewer.querySelector('.slider-counter--total');
        if (current) current.textContent = index + 1;
        if (total) total.textContent = slides.length;
        this.elements.viewer.querySelectorAll('.slider-button').forEach((button) => {
          button.disabled = button.name === 'next' ? index === slides.length - 1 : index === 0;
        });
      }

      disconnectedCallback() {
        if (this.onViewportChange) this.mql.removeEventListener('change', this.onViewportChange);
        this.navigationResizeObserver?.disconnect();
        cancelAnimationFrame(this.navigationFrame);
        window.clearInterval(this.autoplayTimer);
        window.clearTimeout(this.autoplayResumeTimer);
        this.autoplayController?.abort();
      }

      onSlideChanged(event) {
        const thumbnail = this.elements.thumbnails.querySelector(
          `[data-target="${event.detail.currentElement.dataset.mediaId}"]`
        );
        this.setActiveThumbnail(thumbnail);
      }

      setActiveMedia(mediaId, prepend) {
        if (this.dataset.disablePrepend === 'true') prepend = false;
        const activeMedia =
          this.elements.viewer.querySelector(`[data-media-id="${mediaId}"]`) ||
          this.elements.viewer.querySelector('[data-media-id]');
        if (!activeMedia) {
          return;
        }
        this.elements.viewer.querySelectorAll('[data-media-id]').forEach((element) => {
          element.classList.remove('is-active');
        });
        activeMedia?.classList?.add('is-active');

        if (prepend) {
          activeMedia.parentElement.firstChild !== activeMedia && activeMedia.parentElement.prepend(activeMedia);

          if (this.elements.thumbnails) {
            const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
            activeThumbnail.parentElement.firstChild !== activeThumbnail && activeThumbnail.parentElement.prepend(activeThumbnail);
          }

          if (this.elements.viewer.slider) this.elements.viewer.resetPages();
        }

        this.preventStickyHeader();
        window.setTimeout(() => {
          if (!this.mql.matches || this.elements.thumbnails) {
            activeMedia.parentElement.scrollTo({ left: activeMedia.offsetLeft });
          }
          const activeMediaRect = activeMedia.getBoundingClientRect();
          // Don't scroll if the image is already in view
          if (activeMediaRect.top > -0.5) return;
          const top = activeMediaRect.top + window.scrollY;
          window.scrollTo({ top: top, behavior: 'smooth' });
        });
        this.playActiveMedia(activeMedia);
        this.updateConfiguredNavigation();

        if (!this.elements.thumbnails) return;
        const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
        this.setActiveThumbnail(activeThumbnail);
        this.announceLiveRegion(activeMedia, activeThumbnail.dataset.mediaPosition);
      }

      setActiveThumbnail(thumbnail) {
        if (!this.elements.thumbnails || !thumbnail) return;

        this.elements.thumbnails
          .querySelectorAll('button')
          .forEach((element) => element.removeAttribute('aria-current'));
        thumbnail.querySelector('button').setAttribute('aria-current', true);
        if (this.elements.thumbnails.isSlideVisible(thumbnail, 10)) return;

        const isVertical =
          window.innerWidth < 750
            ? this.elements.thumbnails.dataset.mobileVertical === 'true'
            : this.elements.thumbnails.dataset.desktopVertical === 'true';
        this.elements.thumbnails.slider.scrollTo(
          isVertical
            ? { top: thumbnail.offsetTop, behavior: 'smooth' }
            : { left: thumbnail.offsetLeft, behavior: 'smooth' }
        );
      }

      announceLiveRegion(activeItem, position) {
        const image = activeItem.querySelector('.product__modal-opener--image img');
        if (!image) return;
        image.onload = () => {
          this.elements.liveRegion.setAttribute('aria-hidden', false);
          this.elements.liveRegion.innerHTML = window.accessibilityStrings.imageAvailable.replace('[index]', position);
          setTimeout(() => {
            this.elements.liveRegion.setAttribute('aria-hidden', true);
          }, 2000);
        };
        image.src = image.src;
      }

      playActiveMedia(activeItem) {
        window.pauseAllMedia();
        const deferredMedia = activeItem.querySelector('.deferred-media');
        if (deferredMedia) deferredMedia.loadContent(false);
      }

      preventStickyHeader() {
        this.stickyHeader = this.stickyHeader || document.querySelector('sticky-header');
        if (!this.stickyHeader) return;
        this.stickyHeader.dispatchEvent(new Event('preventHeaderReveal'));
      }

      removeListSemantic() {
        if (!this.elements.viewer.slider) return;
        this.elements.viewer.slider.setAttribute('role', 'presentation');
        this.elements.viewer.sliderItems.forEach((slide) => slide.setAttribute('role', 'presentation'));
      }
    }
  );
}
